import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Gift, Copy, Check, X, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { shopApi } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { toast } from "sonner";

export default function ReferralWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data } = useQuery({
    queryKey: ["myReferral"],
    queryFn: shopApi.myReferral,
    enabled: !!user && open, // only fetch when opened
  });

  // Hide widget entirely for signed-out visitors (they'd need to log in first)
  if (!user) return null;

  const referralUrl = data?.referral_code
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${data.referral_code}`
    : "";

  const copyLink = () => {
    if (!referralUrl) return;
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareText = `Save 10% off your first order at NovalynxTech — I love this place: ${referralUrl}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "NovalynxTech", text: shareText, url: referralUrl }); }
      catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-30 h-11 pl-3 pr-4 rounded-full bg-primary text-black font-medium text-sm shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
        data-testid="referral-widget-trigger"
        aria-label="Refer a friend"
      >
        <Gift className="h-4 w-4" fill="currentColor" />
        Give $10, Get $10
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md" data-testid="referral-modal">
          <DialogHeader>
            <DialogTitle className="text-xl">Share NovalynxTech, earn rewards</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Send friends your link. When they place their first order, <span className="text-primary font-medium">they save 10%</span> and <span className="text-primary font-medium">you get $10 in store credit</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-secondary/40 p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Invited</p>
                <p className="text-xl font-bold text-primary mt-1">{data?.invited_count ?? "—"}</p>
              </div>
              <div className="rounded-xl bg-secondary/40 p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Rewarded</p>
                <p className="text-xl font-bold text-primary mt-1">{data?.rewarded_count ?? "—"}</p>
              </div>
              <div className="rounded-xl bg-secondary/40 p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Balance</p>
                <p className="text-xl font-bold text-primary mt-1">${(data?.store_credit ?? 0).toFixed(0)}</p>
              </div>
            </div>

            {/* Link */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Your unique link</p>
              <button
                onClick={copyLink}
                className="w-full py-3 px-4 rounded-xl bg-black border border-primary/40 font-mono text-xs text-primary flex items-center justify-between gap-3 hover:bg-primary/10 transition"
                data-testid="referral-copy-link"
              >
                <span className="truncate">{referralUrl || "Loading…"}</span>
                {copied ? <Check className="h-4 w-4 shrink-0" /> : <Copy className="h-4 w-4 shrink-0" />}
              </button>
            </div>

            {/* Share buttons */}
            <div className="grid grid-cols-4 gap-2">
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-white/10 hover:border-primary/40 p-2 text-center transition"
                data-testid="referral-share-whatsapp"
              >
                <div className="text-lg">💬</div>
                <div className="text-[10px] mt-1">WhatsApp</div>
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-white/10 hover:border-primary/40 p-2 text-center transition"
                data-testid="referral-share-x"
              >
                <div className="text-lg">𝕏</div>
                <div className="text-[10px] mt-1">Post</div>
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent("Save 10% at NovalynxTech")}&body=${encodeURIComponent(shareText)}`}
                className="rounded-xl border border-white/10 hover:border-primary/40 p-2 text-center transition"
                data-testid="referral-share-email"
              >
                <div className="text-lg">✉️</div>
                <div className="text-[10px] mt-1">Email</div>
              </a>
              <button
                onClick={nativeShare}
                className="rounded-xl border border-white/10 hover:border-primary/40 p-2 text-center transition"
                data-testid="referral-share-more"
              >
                <Share2 className="h-4 w-4 mx-auto" />
                <div className="text-[10px] mt-1">More…</div>
              </button>
            </div>

            <p className="text-[10px] text-muted-foreground text-center">
              Store credit is added automatically once your friend's order ships.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
